/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import UserSegmentToggle from '../common/UserSegmentToggle';
import { useUserSegment, watchParam } from '../../context/UserSegmentContext';
import ExportButton from '../active-users/ExportButton';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import { PERMISSIONS, permissionsParam, chipState } from './permissionMeta';
import PermissionFilters from './PermissionFilters';
import PlatformGrantPanels from './PlatformGrantPanels';
import PermissionGrantRateChart from './PermissionGrantRateChart';
import PermissionMatrixTable from './PermissionMatrixTable';
import ColumnVisibilityMenu from './ColumnVisibilityMenu';
import '../active-users/active-users.css';
import './user-permissions.css';

const LIMIT = 20;
const EXPORT_LIMIT = 100000;
const CHIP_TEXT = { granted: 'Granted', denied: 'Denied', na: 'NA' };

// CSV column set (fixed identity + all permissions + timestamps).
const EXPORT_COLUMNS = [
  { key: 'user_id', label: 'No' },
  { key: 'name', label: 'Name' },
  { key: 'platform', label: 'Platform' },
  { key: 'mobile_app_version', label: 'Mobile app version' },
  { key: 'watch_app_version', label: 'Watch app version' },
  ...PERMISSIONS.map((p) => ({ key: p.key, label: p.label })),
  { key: 'last_updated_mobile', label: 'Last updated (Mobile)' },
  { key: 'last_updated_watch', label: 'Last updated (Watch)' },
];

// Grants exported as stable text tokens (Granted / Denied / NA).
const toCsvRow = (u) => {
  const out = { ...u };
  PERMISSIONS.forEach((p) => { out[p.key] = CHIP_TEXT[chipState(u[p.key])]; });
  return out;
};

const UserPermissions = () => {
  const [name] = useState('User Permissions');
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [users, setUsers] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [permCounts, setPermCounts] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('');
  const [permissions, setPermissions] = useState({});   // { key: true }
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [hidden, setHidden] = useState([]);              // hidden permission columns
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { segment } = useUserSegment();   // global watch/without/all segment (feature 023)

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(
    async (config) => {
      const now = new Date();
      if (expire * 1000 < now.getTime()) {
        const r = await axios.get(`${baseUrl}/admin/api/token`);
        config.headers.Authorization = `Bearer ${r.data.accessToken}`;
        setToken(r.data.accessToken);
        setExpire(jwtDecode(r.data.accessToken).exp);
      }
      return config;
    },
    (err) => Promise.reject(err)
  );

  useEffect(() => { refreshToken(); }, []);
  useEffect(() => { load(1, '', '', {}, 'id', 'ASC'); }, []);

  // Re-fetch when the global segment changes (skip the initial mount — the load
  // effect above already fires the first request with the default 'watch' segment).
  const didMountSegment = useRef(false);
  useEffect(() => {
    if (!didMountSegment.current) { didMountSegment.current = true; return; }
    reload({ page: 1 });
  }, [segment]);

  const refreshToken = async () => {
    try {
      const r = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(r.data.accessToken);
      setExpire(jwtDecode(r.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const buildUrl = (pg, s, plat, permsObj, sf, so, limit = LIMIT) => {
    const perms = permissionsParam(permsObj);
    let url = `${baseUrl}/admin/api/user-permissions?page=${pg}&limit=${limit}` +
      `&search=${encodeURIComponent(s || '')}&platform=${encodeURIComponent(plat || '')}` +
      `&sortField=${sf}&sortOrder=${so}`;
    if (perms) url += `&permissions=${perms}`;
    url += watchParam(segment);   // watch segment default; omitted when 'all' (feature 023)
    return url;
  };

  const load = async (pg, s, plat, permsObj, sf, so) => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosJWT.get(buildUrl(pg, s, plat, permsObj, sf, so), auth());
      setUsers(res.data.data || []);
      setUsersData(res.data.usersData || []);
      setPermCounts(res.data.userPermissionCount || []);
      setTotalItem(res.data.totalItem || 0);
    } catch (err) {
      if (err.response && err.response.status === 401) { navigate('/'); return; }
      setError('We couldn’t load user permissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch the full filtered set (unpaginated) for CSV export.
  const fetchExportRows = async () => {
    const res = await axiosJWT.get(buildUrl(1, search, platform, permissions, sortField, sortOrder, EXPORT_LIMIT), auth());
    return (res.data.data || []).map(toCsvRow);
  };

  const reload = (over = {}) => {
    const next = {
      page: 1, search, platform, permissions, sortField, sortOrder, ...over,
    };
    setPage(next.page);
    load(next.page, next.search, next.platform, next.permissions, next.sortField, next.sortOrder);
  };

  const handleSearch = () => reload({ page: 1 });
  const handlePlatform = (v) => { setPlatform(v); reload({ platform: v, page: 1 }); };

  const handlePermissionToggle = (key) => {
    const updated = { ...permissions, [key]: !permissions[key] };
    if (!updated[key]) delete updated[key];
    setPermissions(updated);
    reload({ permissions: updated, page: 1 });
  };

  const clearPermissions = () => { setPermissions({}); reload({ permissions: {}, page: 1 }); };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field); setSortOrder(order);
    reload({ sortField: field, sortOrder: order });
  };

  const handlePage = (pg) => {
    setPage(pg);
    load(pg, search, platform, permissions, sortField, sortOrder);
  };

  const handleColumnToggle = (key) =>
    setHidden((h) => (h.includes(key) ? h.filter((k) => k !== key) : [...h, key]));

  const clearFilter = () => {
    setSearch(''); setPlatform(''); setPermissions({});
    setSortField('id'); setSortOrder('ASC'); setPage(1);
    load(1, '', '', {}, 'id', 'ASC');
  };

  const selectedKeys = Object.keys(permissions).filter((k) => permissions[k]);
  const visibleColumns = PERMISSIONS.map((p) => p.key).filter((k) => !hidden.includes(k));
  const hasFilters = Boolean(search || platform || selectedKeys.length);

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name={name} />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">People › <b>Permissions</b></span>
            <span className="au-route">/user-permissions</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Permission grants</h1>

          <PermissionFilters
            searchValue={search}
            platform={platform}
            permissions={permissions}
            showClear={hasFilters}
            onSearchChange={setSearch}
            onPlatformChange={handlePlatform}
            onPermissionToggle={handlePermissionToggle}
            onClearPermissions={clearPermissions}
            onSearch={handleSearch}
            onClear={clearFilter}
          />

          <PlatformGrantPanels counts={permCounts?.[0]} summary={usersData?.[0]} selected={selectedKeys} />
          <PermissionGrantRateChart counts={permCounts?.[0]} summary={usersData?.[0]} selected={selectedKeys} />

          {loading ? (
            <div className="au-panel"><TableSkeleton rows={8} /></div>
          ) : error ? (
            <ErrorState message={error} onRetry={() => load(page, search, platform, permissions, sortField, sortOrder)} />
          ) : users.length === 0 ? (
            <EmptyState
              title="No permission records"
              hasFilters={hasFilters}
              onClear={clearFilter}
              message={hasFilters ? 'No users match the current filters.' : 'There are no permission records to show yet.'}
            />
          ) : (
            <div className="au-panel">
              <div className="au-panel__head">
                <h4 className="au-panel__title">Grant matrix</h4>
                <span className="up-count tnum">{Number(totalItem).toLocaleString()} users match</span>
                <span className="au-spacer" />
                <ColumnVisibilityMenu hidden={hidden} onToggle={handleColumnToggle} />
                <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExportRows} filename="user-permissions.csv" />
              </div>
              <PermissionMatrixTable
                users={users}
                page={page}
                visibleColumns={visibleColumns}
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
              {totalItem > LIMIT && (
                <Pagination
                  activePage={page}
                  itemsCountPerPage={LIMIT}
                  totalItemsCount={totalItem}
                  pageRangeDisplayed={5}
                  onChange={handlePage}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPermissions;
